import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import SportFilter from '../components/shared/SportFilter';
import { toggleBetSlip } from '../utils/betSlip';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_API_BASE_URL;
const SPORT = 'basketball';
const LABEL = 'Basketball';

const css = `
  .sp-leagues { display: flex; gap: 8px; padding: 10px 14px 8px; overflow-x: auto; }
  .sp-leagues::-webkit-scrollbar { display: none; }
  .sp-league-btn { display: flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 20px; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); color: #a0a8b8; font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: all .2s; flex-shrink: 0; }
  .sp-league-btn.active { border-color: rgba(0,212,170,.6); background: rgba(0,212,170,.1); color: #00d4aa; }
  .sp-league-btn img { width: 16px; height: 16px; object-fit: contain; }
  .sp-show-more { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); color: #5a5972; cursor: pointer; flex-shrink: 0; font-size: 16px; font-weight: 700; margin: auto 0; }
  .sp-list { padding: 6px 14px 80px; }
  .sp-card { background: rgba(255,255,255,.02); border: 1px solid rgba(255,255,255,.06); border-radius: 14px; margin-bottom: 10px; overflow: hidden; cursor: pointer; }
  .sp-card:active { border-color: rgba(0,212,170,.3); }
  .sp-card-head { display: flex; align-items: center; justify-content: space-between; padding: 7px 12px; border-bottom: 1px solid rgba(255,255,255,.04); }
  .sp-card-league { display: flex; align-items: center; gap: 6px; }
  .sp-card-league img { width: 14px; height: 14px; object-fit: contain; }
  .sp-card-league-name { font-size: 11px; color: #5a5972; font-weight: 600; }
  .sp-card-date { font-size: 11px; color: #5a5972; }
  .sp-card-body { display: flex; align-items: center; padding: 10px 12px; gap: 10px; }
  .sp-teams { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .sp-team { display: flex; align-items: center; gap: 8px; }
  .sp-team img { width: 20px; height: 20px; object-fit: contain; flex-shrink: 0; }
  .sp-team-name { font-size: 13px; font-weight: 600; color: #e4e7ec; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sp-odds { display: flex; gap: 5px; flex-shrink: 0; }
  .sp-odd-btn { display: flex; flex-direction: column; align-items: center; padding: 7px 8px; min-width: 42px; border-radius: 8px; border: 1px solid rgba(255,255,255,.07); background: rgba(255,255,255,.03); cursor: pointer; }
  .sp-odd-btn.selected { border-color: rgba(0,212,170,.6); background: rgba(0,212,170,.12); }
  .sp-odd-label { font-size: 10px; color: #5a5972; font-weight: 700; margin-bottom: 2px; }
  .sp-odd-value { font-size: 12px; color: #e4e7ec; font-weight: 700; }
  .sp-odd-btn.selected .sp-odd-label, .sp-odd-btn.selected .sp-odd-value { color: #00d4aa; }
  .sp-no-odds { font-size: 11px; color: #3a3857; }
  .sp-spinner { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; gap: 14px; }
  .sp-spinner-ring { width: 36px; height: 36px; border: 3px solid rgba(255,255,255,.06); border-top-color: #00d4aa; border-radius: 50%; animation: sp-spin 1s linear infinite; }
  @keyframes sp-spin { to { transform: rotate(360deg); } }
  .sp-spinner p { color: #5a5972; font-size: 13px; margin: 0; }
  .sp-empty { display: flex; align-items: center; justify-content: center; padding: 60px 20px; }
  .sp-empty p { color: #3a3857; font-size: 13px; }
`;

function fmtDate(ts) {
  return new Date(ts * 1000).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function parseOdds(game) {
  if (!game.odds?.match_winner?.length) return [];
  return game.odds.match_winner.map((o, i) => ({
    label: o.label === 'Home' ? '1' : o.label === 'Away' ? '2' : o.label,
    betType: `Match Winner: ${o.label}`,
    value: o.odd, index: i,
  }));
}

export default function Basketball() {
  const { t } = useLang();
  const [leagues, setLeagues] = useState([]);
  const [sel, setSel] = useState(null);
  const [games, setGames] = useState([]);
  const [visible, setVisible] = useState([]);
  const [loadL, setLoadL] = useState(true);
  const [loadG, setLoadG] = useState(false);
  const [vCount, setVCount] = useState(10);
  const [vLeagues, setVLeagues] = useState(5);
  const [selBets, setSelBets] = useState([]);
  const lmRef = useRef(); const obsRef = useRef();
  const userInteractedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const sync = () => setSelBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]').map(b => b.id));
    sync(); window.addEventListener('betAdded', sync);
    return () => window.removeEventListener('betAdded', sync);
  }, []);

  useEffect(() => { fetchLeagues(); }, []);
  useEffect(() => { if (sel) { setVCount(10); fetchGames(sel.id); } }, [sel]);
  useEffect(() => { setVisible(games.slice(0, vCount)); }, [games, vCount]);

  const loadMore = useCallback(() => { if (vCount < games.length) setVCount(p => p + 10); }, [vCount, games.length]);
  useEffect(() => {
    if (obsRef.current) obsRef.current.disconnect();
    obsRef.current = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { threshold: 0.1 });
    if (lmRef.current) obsRef.current.observe(lmRef.current);
    return () => { if (obsRef.current) obsRef.current.disconnect(); };
  }, [loadMore]);

  const fetchLeagues = async () => {
    setLoadL(true);
    try {
      const k = `${SPORT}_leagues`; const c = localStorage.getItem(k);
      if (c) { const { data, ts } = JSON.parse(c); if (Date.now() - ts < 30 * 864e5) { setLeagues(data); autoSelectBestLeague(data); return; } }
      const res = await fetch(`${API}/api-bet/${SPORT}/leagues.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const data = await res.json();
      if (data.success) {
        const list = [...(data.top_leagues || []), ...(data.other_leagues || [])].map(l => ({ id: l.id, name: l.name, logo: l.logo }));
        localStorage.setItem(k, JSON.stringify({ data: list, ts: Date.now() }));
        setLeagues(list); if (list.length) autoSelectBestLeague(list);
      }
    } catch (e) { console.error(e); } finally { setLoadL(false); }
  };

  // Chantier 3 : sélectionne la 1re ligue qui a des matchs (la saison est
  // auto-détectée côté backend, donc pas de param season à envoyer ici).
  const autoSelectBestLeague = async (list) => {
    if (list.length) setLoadG(true); // spinner pendant le sondage (évite un flash "aucun match")
    const candidates = list.slice(0, 12);
    for (const lg of candidates) {
      if (userInteractedRef.current) return;
      const data = await loadGamesData(lg.id);
      if (userInteractedRef.current) return;
      if (data.length > 0) { setSel(lg); return; }
    }
    if (!userInteractedRef.current && list.length > 0) setSel(list[0]);
  };

  // Charge les matchs d'une ligue SANS toucher au state (réutilisé par l'auto-sélection).
  const loadGamesData = async (lid) => {
    const k = `${SPORT}_games_${lid}`;
    try {
      const c = localStorage.getItem(k);
      if (c) { const { data, ts } = JSON.parse(c); if (Date.now() - ts < 36e5) { const v = data.filter(g => (g.timestamp || 0) * 1000 > Date.now()); if (v.length) return v; } }
    } catch {}
    try {
      const res = await fetch(`${API}/api-bet/${SPORT}/matchs.php?league=${lid}`);
      const data = await res.json();
      if (data.success) {
        const v = (data.games || data.fixtures || []).filter(g => (g.timestamp || g.fixture?.timestamp || 0) * 1000 > Date.now());
        localStorage.setItem(k, JSON.stringify({ data: v, ts: Date.now() }));
        return v;
      }
    } catch (e) { console.error(e); }
    return [];
  };

  const fetchGames = async (lid) => {
    setLoadG(true);
    const v = await loadGamesData(lid);
    setGames(v);
    setLoadG(false);
  };

  const handleOdd = (e, game, betType, oddLabel, oddValue, oddIndex) => {
    e.stopPropagation();
    const [market] = betType.split(':');
    const fixtureDate = new Date(game.timestamp * 1000).toISOString();
    toggleBetSlip({ id: `${SPORT}-${game.id}-${oddIndex}`, matchId: game.id, match: `${game.teams?.home?.name || '?'} vs ${game.teams?.away?.name || '?'}`, homeTeam: game.teams?.home?.name, awayTeam: game.teams?.away?.name, date: new Date(game.timestamp * 1000).toLocaleString('fr-FR'), fixtureDate, selection: betType, betType: market.trim(), betLabel: oddLabel, handicap: null, odds: parseFloat(oddValue), league: game.league?.name || LABEL, isLive: false, sport: SPORT });
  };

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <BackHeader title={LABEL} />
      <SportFilter />
      {loadL ? <div className="sp-spinner"><div className="sp-spinner-ring" /><p>{t('sports.loading')}</p></div> : (
        <div className="sp-leagues">
          {leagues.slice(0, vLeagues).map(l => (
            <button key={l.id} className={'sp-league-btn' + (sel?.id === l.id ? ' active' : '')} onClick={() => { userInteractedRef.current = true; setSel(l); }}>
              {l.logo && <img src={l.logo} alt="" />}{l.name}
            </button>
          ))}
          {vLeagues < leagues.length && <button className="sp-show-more" onClick={() => setVLeagues(p => Math.min(p + 5, leagues.length))}>+</button>}
        </div>
      )}
      {loadG ? <div className="sp-spinner"><div className="sp-spinner-ring" /><p>{t('sports.loadingMatches')}</p></div> :
        games.length === 0 && !loadL ? <div className="sp-empty"><p>{t('sports.noUpcoming')}</p></div> : (
        <div className="sp-list">
          {visible.map(game => {
            const odds = parseOdds(game);
            return (
              <div key={game.id} className="sp-card" onClick={() => navigate(`/match/basketball/${game.id}`)}>
                <div className="sp-card-head">
                  <div className="sp-card-league">{game.league?.logo && <img src={game.league.logo} alt="" />}<span className="sp-card-league-name">{game.league?.name}</span></div>
                  <span className="sp-card-date">{fmtDate(game.timestamp)}</span>
                </div>
                <div className="sp-card-body">
                  <div className="sp-teams">
                    <div className="sp-team">{game.teams?.home?.logo && <img src={game.teams.home.logo} alt="" />}<span className="sp-team-name">{game.teams?.home?.name}</span></div>
                    <div className="sp-team">{game.teams?.away?.logo && <img src={game.teams.away.logo} alt="" />}<span className="sp-team-name">{game.teams?.away?.name}</span></div>
                  </div>
                  {odds.length > 0 ? (
                    <div className="sp-odds" onClick={e => e.stopPropagation()}>
                      {odds.map(o => <button key={o.index} className={'sp-odd-btn' + (selBets.includes(`${SPORT}-${game.id}-${o.index}`) ? ' selected' : '')} onClick={e => handleOdd(e, game, o.betType, o.label, o.value, o.index)}><span className="sp-odd-label">{o.label}</span><span className="sp-odd-value">{o.value}</span></button>)}
                    </div>
                  ) : <span className="sp-no-odds">N/A</span>}
                </div>
              </div>
            );
          })}
          {vCount < games.length && <div ref={lmRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="sp-spinner-ring" style={{ width: 24, height: 24, borderWidth: 2 }} /></div>}
        </div>
      )}
    </PhoneShell>
  );
}
